import {
    Component,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    ElementRef,
    AfterViewInit,
    ChangeDetectorRef,
    Renderer2
} from '@angular/core';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { Attachment, OfferType } from 'src/app/shared/models/offers.model';
import { FeatureFlag } from '../../models/enums/feature-flag';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'nova-shared-offer-attachments',
    templateUrl: './offer-attachments.component.html',
    styleUrls: ['./offer-attachments.component.scss'],
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule]
})
export class SharedOfferAttachmentsComponent implements AfterViewInit {
    readonly featureFlag = FeatureFlag;
    document: any = { files: [] };
    validationResults: any[] = [];
    isDragging = false;
    attachmentData: Attachment[];
    uploadInProgress: boolean = false;
    isUpdatingAttachment = false;
    attachmentToUpdate = null;

    @Input()
    set attachments(data: Attachment[]) {
        if (data) {
            this.attachmentData = data;
            if (this.document.files.length) {
                this.clearExistingDocutments(data);
            }
        }
    }

    @Input()
    editMode = false;

    @Input()
    canModify = true;

    @Input() offerType: OfferType;
    @Input() headingTitle = 'Internal Documents';
    @Input() headingTitleSubtext = '(Only Aya employees will see this)';

    @Output() getAttachmentFile = new EventEmitter();
    @Output() attachmentUpload = new EventEmitter();
    @Output() attachmentDelete = new EventEmitter();
    @Output() attachmentUpdate = new EventEmitter();

    @ViewChild('fileInput', { static: false }) fileInput: ElementRef;

    get attachments(): Attachment[] {
        return this.attachmentData;
    }

    constructor(
        private confirmDialogService: ConfirmDialogService,
        private ref: ChangeDetectorRef,
        private _renderer: Renderer2
    ) {}

    ngAfterViewInit() {
        this.ref.detectChanges();
    }

    clearExistingDocutments(data) {
        this.document.files = this.document.files.filter((item) => item === data);
        this.uploadInProgress = false;
        this.isUpdatingAttachment = false;
        this.attachmentToUpdate = null;
    }

    getAttachment(file: Attachment) {
        this.getAttachmentFile.emit(file);
    }

    deleteAttachment(file: any) {
        this.confirmDialogService
            .show(`Delete Document ${file.fileName}?`, 'DELETE', 'Delete Document', true, 'CANCEL')
            .subscribe((result) => {
                if (result) {
                    this.attachmentDelete.emit(file);
                }
            });
    }

    getDroppedFiles(e: DragEvent) {
        this.updateDragState(e);
        this.addFiles(e.dataTransfer.files);
    }

    addNewDocs() {
        this.isUpdatingAttachment = false;
        this.document.attachmentId = null;
        this.fileInput.nativeElement.click();
    }

    replaceAttachment(attachmentId: number) {
        if (attachmentId) {
            this.attachmentToUpdate = attachmentId;
            this.fileInput.nativeElement.removeAttribute('multiple');
            this.fileInput.nativeElement.click();
            this.isUpdatingAttachment = true;
        }
    }

    addSelectedFiles() {
        this._renderer.setAttribute(this.fileInput.nativeElement, 'multiple', '');
        if (this.isUpdatingAttachment && this.attachmentToUpdate) {
            this.document.attachmentId = this.attachmentToUpdate;
        }
        this.addFiles(this.fileInput.nativeElement.files);
        this.fileInput.nativeElement.value = null;
    }

    removeFile(value: File) {
        this.document.files = this.document.files.filter((file) => file.name !== value.name);
        if (!this.document.files.length) {
            this.isUpdatingAttachment = false;
        }
    }

    updateDragState(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'copy';
        }

        this.isDragging = e.type === 'dragover';
    }

    uploadFiles(): void {
        if (this.document.files.length) {
            let fileToUpload = this.document.files;
            if (this.document.files.length > 1) {
                this.uploadInProgress = true;
                fileToUpload.forEach((file) => this.attachmentUpload.emit(file));
            } else {
                fileToUpload = this.document.files[0];
                this.uploadInProgress = true;
                if (this.isUpdatingAttachment) {
                    this.attachmentUpdate.emit(this.document);
                } else {
                    this.attachmentUpload.emit(fileToUpload);
                }
            }
        } else {
            this.isUpdatingAttachment = false;
        }
    }

    private addFiles(value: FileList) {
        this.validationResults = [];
        for (let i = 0; i < value.length; i++) {
            const file = value[i];
            if (this.validateFile(file)) {
                this.document.files.push(file);
                this.document.originalFileName = this.document.files.length > 1 ? 'Multiple files' : file.name;
            }
        }
    }

    private validateFile(file: File): boolean {
        if (file.size > 50 * 1024 * 1024) {
            //50Mb
            this.validationResults.push({
                isValid: false,
                name: file.name,
                message: 'File format exceeds 50MB size limit. Please save the file as a smaller size and try again.'
            });
            return false;
        }

        if (this.document.files.find((f) => f.name === file.name)) {
            this.validationResults.push({
                isValid: false,
                name: file.name,
                message: `There is already a file named ${file.name}. Please save the file with a different file name and try again.`
            });
            return false;
        }

        return true;
    }
}
